import React, { useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import toMoment from '~/lib/toMoment';
import { flashMessage } from '~/app/actions';
import { getStudioEditorPreviewUrl } from '~/lib/studioUrl';
import { pagePublicPath } from '~/lib/pagePublicPath';
import { S, A, TONES, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader } from '~/app/ui/Page';
import EmptyState from '~/app/ui/EmptyState';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { RowMenu, MenuItem } from '~/app/ui/Menu';

export const PENDING_APPROVAL = gql`
  query getPagesPendingApproval {
    pagesNeedApproval {
      _id
      editorType
      title
      path
      is_root
      updatedAt
      createdAt
      updatedBy
      canUserEdit
      canUserApprove
      canUserDecide
      category {
        title
      }
      state
      publishedVersion
      commitedVersion
      commitedBy
      versions {
        _id
        versionId
        notes
      }
    }
  }
`;

const APPROVE_PUBLISH = gql`mutation approvePublish($id: ID!, $version: String) { approvePublishPage(pageId: $id, version: $version) { status message } }`;
const APPROVE_ALL = gql`mutation approveAllPublish($pageIds: [ID]!) { approveAllPublish(pageIds: $pageIds) { status message } }`;
const DECLINE_PUBLISH = gql`mutation declinePublish($id: ID!, $reason: String, $version: String) { declinePublishPage(pageId: $id, reason: $reason, version: $version) { status message } }`;
const APPROVE_DELETE = gql`mutation approveDelete($id: ID!) { approveDeletePage(pageId: $id) { status message } }`;
const DECLINE_DELETE = gql`mutation declineDelete($id: ID!) { declineDeletePage(pageId: $id) { status message } }`;
const APPROVE_REMOVE = gql`mutation approveRemove($id: ID!) { approveRemovePage(pageId: $id) { status message } }`;
const DECLINE_REMOVE = gql`mutation declineRemove($id: ID!) { declineRemovePage(pageId: $id) { status message } }`;

const REFETCH = ['getPages', 'getPageSiteCounts', 'getPagesPendingApproval', 'getNavData', 'getPendingDeployment'];

type Kind = 'UNDER_REVIEW' | 'PENDING_REMOVAL' | 'PENDING_DELETE';
type Request = {
  _id: string;
  editorType?: string | null;
  title: string;
  path: string;
  updatedAt?: string | null;
  updatedBy?: string | null;
  canUserEdit?: boolean;
  canUserApprove?: boolean;
  /** Category approver: may decline, and decide deletions and withdrawals (AIXC deployers may only approve publishing). */
  canUserDecide?: boolean;
  category?: { title?: string | null } | null;
  is_root?: boolean;
  state: Kind;
  publishedVersion?: string | null;
  commitedVersion?: string | null;
  commitedBy?: string | null;
  versions?: Array<{ versionId: string; notes?: string | null }> | null;
};

type Pending =
  | { action: 'approve' | 'decline'; request: Request }
  | { action: 'approveAll'; requests: Request[] };

const KINDS: Record<Kind, { label: string; icon: string; tone: keyof typeof TONES }> = {
  UNDER_REVIEW: { label: 'Publish', icon: 'publish', tone: 'amber' },
  PENDING_REMOVAL: { label: 'Withdraw', icon: 'visibility_off', tone: 'red' },
  PENDING_DELETE: { label: 'Delete', icon: 'delete_outline', tone: 'red' }
};

const useStyles = createUseStyles({
  list: { display: 'grid', gap: 10 },
  card: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    gap: '4px 14px',
    alignItems: 'start',
    padding: '16px 16px 16px 14px',
    background: A.surface,
    border: `1px solid ${S.line}`,
    borderRadius: 12,
    '@media (max-width: 720px)': { gridTemplateColumns: 'auto minmax(0, 1fr)' }
  },
  kind: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: 10,
    '& .material-icons': { fontSize: 20 }
  },
  main: { minWidth: 0, display: 'grid', gap: 4 },
  top: { display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  kindLabel: { fontFamily: S.label, fontSize: 12, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' },
  title: { fontSize: 16, fontWeight: 600, margin: 0, color: S.fg },
  path: { fontFamily: S.mono, fontSize: 12, color: S.muted, wordBreak: 'break-all' },
  note: {
    margin: '4px 0 0',
    padding: '8px 12px',
    borderLeft: `3px solid ${S.line}`,
    background: S.chrome,
    borderRadius: '0 8px 8px 0',
    fontSize: 14,
    lineHeight: 1.45,
    color: '#33363C',
    [DARK]: { color: S.fg },
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    whiteSpace: 'pre-wrap'
  },
  noNote: { fontSize: 13, color: S.muted, fontStyle: 'italic' },
  meta: { fontSize: 13, color: S.muted },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '@media (max-width: 720px)': { gridColumn: '2', marginTop: 8 }
  },
  waiting: { fontSize: 13, color: S.muted, whiteSpace: 'nowrap' },
  reason: {
    boxSizing: 'border-box',
    width: '100%',
    minHeight: 84,
    marginTop: 4,
    padding: 10,
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 8,
    fontFamily: S.ui,
    fontSize: 14,
    lineHeight: 1.45,
    resize: 'vertical',
    '&:focus': { outline: 'none', borderColor: S.fg, boxShadow: `0 0 0 1px ${S.fg}` }
  },
  list2: { margin: '6px 0 0', paddingLeft: 18, maxHeight: 180, overflowY: 'auto', '& li': { margin: '2px 0' } }
});

const requester = (r: Request) => (r.state === 'UNDER_REVIEW' ? (r.commitedBy || r.updatedBy) : r.updatedBy);
const noteOf = (r: Request) => {
  if (r.state !== 'UNDER_REVIEW' || !r.versions) return null;
  const v = r.versions.find(x => x.versionId === r.commitedVersion);
  return v && v.notes && v.notes.trim() ? v.notes.trim() : null;
};

/** Throws the server's message unless it says the action went through. */
const ensureOk = (response?: { status: number; message?: string } | null) => {
  if (!response || response.status !== 200) throw new Error((response && response.message) || 'That didn’t go through. Try again.');
};

const ApprovalsScreen: React.FC = () => {
  const c = useStyles();
  const dispatch = useDispatch();
  // One list for every kind of request. Links to the old per-kind tabs (and Deleted pages) land on it.
  const { kind } = useParams<{ kind?: string }>();
  const isSuperAdmin = useSelector((state: { app: { user?: { isSuperAdmin?: boolean } } }) => !!state.app.user?.isSuperAdmin);
  const { data, loading, error, refetch } = useQuery(PENDING_APPROVAL, { fetchPolicy: 'cache-and-network' });
  const [pending, setPending] = useState<Pending | null>(null);
  const [reason, setReason] = useState('');

  const opts = { refetchQueries: REFETCH, awaitRefetchQueries: true };
  const [approvePublish] = useMutation(APPROVE_PUBLISH, opts);
  const [approveAll] = useMutation(APPROVE_ALL, opts);
  const [declinePublish] = useMutation(DECLINE_PUBLISH, opts);
  const [approveDelete] = useMutation(APPROVE_DELETE, opts);
  const [declineDelete] = useMutation(DECLINE_DELETE, opts);
  const [approveRemove] = useMutation(APPROVE_REMOVE, opts);
  const [declineRemove] = useMutation(DECLINE_REMOVE, opts);

  const requests: Request[] = useMemo(() => {
    const all: Request[] = (data?.pagesNeedApproval || []).filter((r: Request) => KINDS[r.state]);
    // Newest first.
    return all.slice().sort((a, b) => (toMoment(b.updatedAt)?.valueOf() || 0) - (toMoment(a.updatedAt)?.valueOf() || 0));
  }, [data]);
  const approvablePublish = requests.filter(r => r.state === 'UNDER_REVIEW' && r.canUserApprove);

  const run = async () => {
    if (!pending) return;
    if (pending.action === 'approveAll') {
      const r = await approveAll({ variables: { pageIds: pending.requests.map(x => x._id) } });
      ensureOk(r.data?.approveAllPublish);
      dispatch(flashMessage(`Approved ${pending.requests.length} publish requests. They go live with the next deployment.`));
      return;
    }
    const { action, request } = pending;
    const id = request._id;
    let response;
    if (request.state === 'UNDER_REVIEW') {
      response = action === 'approve'
        // The version shown in the list: if the request changed since, the server refuses instead of deciding on another one.
        ? (await approvePublish({ variables: { id, version: request.commitedVersion || null } })).data?.approvePublishPage
        : (await declinePublish({ variables: { id, reason: reason.trim() || null, version: request.commitedVersion || null } })).data?.declinePublishPage;
    } else if (request.state === 'PENDING_REMOVAL') {
      response = action === 'approve'
        ? (await approveRemove({ variables: { id } })).data?.approveRemovePage
        : (await declineRemove({ variables: { id } })).data?.declineRemovePage;
    } else {
      response = action === 'approve'
        ? (await approveDelete({ variables: { id } })).data?.approveDeletePage
        : (await declineDelete({ variables: { id } })).data?.declineDeletePage;
    }
    ensureOk(response);
    const done = {
      UNDER_REVIEW: action === 'approve' ? `Approved “${request.title}”. It goes live with the next deployment.` : `Declined the publish request for “${request.title}”.`,
      PENDING_REMOVAL: action === 'approve' ? `Approved. “${request.title}” comes off the site with the next deployment.` : `Declined. “${request.title}” stays on the site.`,
      PENDING_DELETE: action === 'approve' ? `Deleted “${request.title}”.` : `Declined. “${request.title}” is kept.`
    }[request.state];
    dispatch(flashMessage(done));
  };

  const open = (p: Pending) => { setReason(''); setPending(p); };

  const renderActions = (r: Request) => {
    const studio = r.editorType === 'studio';
    const previewRequested = studio ? getStudioEditorPreviewUrl(r._id) : `${r.path}?preview=1&v=${encodeURIComponent(r.commitedVersion || 'DRAFT')}`;
    const menu: MenuItem[][] = [
      [
        ...(r.state === 'UNDER_REVIEW' ? [{ key: 'preview', label: r.commitedVersion ? `Preview v${r.commitedVersion}` : 'Preview', icon: 'visibility', hint: 'Opens in a new tab', href: previewRequested, newTab: true }] : []),
        ...(r.state !== 'UNDER_REVIEW' && !studio && r.publishedVersion ? [{ key: 'live', label: `Preview live v${r.publishedVersion}`, icon: 'visibility', hint: 'Opens in a new tab', href: `${r.path}?preview=1&v=${encodeURIComponent(r.publishedVersion)}`, newTab: true }] : []),
        ...(!studio && r.state !== 'PENDING_DELETE' && r.canUserEdit ? [{ key: 'edit', label: 'Open in editor', icon: 'edit', to: `/admin/page/${r._id}` }] : [])
      ],
      r.canUserApprove && r.state === 'UNDER_REVIEW' && !studio
        ? [{ key: 'approve', label: 'Approve without reviewing…', icon: 'done', onSelect: () => open({ action: 'approve', request: r }) }]
        : [],
      r.canUserApprove && r.canUserDecide && r.state === 'UNDER_REVIEW'
        ? [{ key: 'decline', label: 'Decline…', icon: 'block', danger: true, onSelect: () => open({ action: 'decline', request: r }) }]
        : []
    ];
    let main: React.ReactNode = null;
    if (!r.canUserApprove || (r.state !== 'UNDER_REVIEW' && !r.canUserDecide)) {
      main = <span className={c.waiting}>Waiting for an approver</span>;
    } else if (r.state === 'UNDER_REVIEW') {
      main = studio
        ? (
          <>
            <Button href={previewRequested} target="_blank" icon="open_in_new">Preview</Button>
            <Button variant="primary" icon="done" onClick={() => open({ action: 'approve', request: r })}>Approve…</Button>
          </>
        )
        : <Button variant="primary" to={`/admin/review/${r._id}`} icon="rate_review" data-testid={`review-${r._id}`}>Review</Button>;
    } else {
      main = (
        <>
          <Button onClick={() => open({ action: 'decline', request: r })}>Decline</Button>
          <Button variant="primary" onClick={() => open({ action: 'approve', request: r })}>
            {r.state === 'PENDING_DELETE' ? 'Approve deletion…' : 'Approve withdrawal…'}
          </Button>
        </>
      );
    }
    return (
      <div className={c.actions}>
        {main}
        <RowMenu label={`More for ${r.title}`} groups={menu} />
      </div>
    );
  };

  const renderRequest = (r: Request) => {
    const k = KINDS[r.state];
    const tone = TONES[k.tone];
    const when = toMoment(r.updatedAt);
    const who = requester(r);
    const note = noteOf(r);
    return (
      <article key={r._id} className={c.card} data-request={r._id} data-kind={r.state}>
        <span className={c.kind} style={{ background: tone.bg, color: tone.fg }} aria-hidden>
          <i className="material-icons">{k.icon}</i>
        </span>
        <div className={c.main}>
          <div className={c.top}>
            <span className={c.kindLabel} style={{ color: tone.fg }}>
              {r.state === 'UNDER_REVIEW' && r.commitedVersion ? `${k.label} v${r.commitedVersion}` : k.label}
            </span>
            <h3 className={c.title}>{r.title}</h3>
          </div>
          <div className={c.path}>{pagePublicPath(r.path)}</div>
          {r.state === 'UNDER_REVIEW' && (note ? <p className={c.note} data-testid="request-note">{note}</p> : <span className={c.noNote}>No note from the author.</span>)}
          <div className={c.meta}>
            {who ? `Asked by ${who}` : 'Asked'}
            {when ? <span title={when.format('MMM D, YYYY h:mm A')}>{` · ${when.fromNow()}`}</span> : null}
            {` · ${r.is_root ? 'Root' : (r.category?.title || 'Uncategorized').replace(/cxc10/gi, 'AIXC')}`}
            {r.editorType === 'studio' ? ' · Studio page' : ''}
          </div>
        </div>
        {renderActions(r)}
      </article>
    );
  };

  const dialog = (() => {
    if (!pending) return null;
    if (pending.action === 'approveAll') {
      return (
        <ConfirmDialog
          title={`Approve ${pending.requests.length} publish requests?`}
          confirmLabel={`Approve ${pending.requests.length}`}
          onConfirm={run}
          onClose={() => setPending(null)}
        >
          <p>They go live with the next deployment. Nobody will review the changes first.</p>
          <ul className={c.list2}>{pending.requests.map(r => <li key={r._id}>{r.title}{r.commitedVersion ? ` (v${r.commitedVersion})` : ''}</li>)}</ul>
        </ConfirmDialog>
      );
    }
    const { action, request: r } = pending;
    if (r.state === 'UNDER_REVIEW') {
      return action === 'approve' ? (
        <ConfirmDialog title={`Approve “${r.title}”${r.commitedVersion ? ` v${r.commitedVersion}` : ''}?`} confirmLabel="Approve" onConfirm={run} onClose={() => setPending(null)}>
          <p>It goes live with the next deployment.{r.editorType !== 'studio' ? (r.publishedVersion ? ' Reviewing first shows exactly what changed.' : ' Reviewing first shows the whole page.') : ''}</p>
        </ConfirmDialog>
      ) : (
        <ConfirmDialog title={`Decline “${r.title}”?`} confirmLabel="Decline request" danger onConfirm={run} onClose={() => setPending(null)}>
          <p>The page stays as it is. Tell {requester(r) || 'the author'} what to change; they’ll see it in the page’s history.</p>
          <label htmlFor="list-decline-reason" style={{ fontWeight: 600, fontSize: 13 }}>Reason (optional)</label>
          <textarea id="list-decline-reason" className={c.reason} value={reason} onChange={e => setReason(e.target.value)} maxLength={1000} />
        </ConfirmDialog>
      );
    }
    if (r.state === 'PENDING_REMOVAL') {
      return action === 'approve' ? (
        <ConfirmDialog title={`Withdraw “${r.title}” from the site?`} confirmLabel="Approve withdrawal" onConfirm={run} onClose={() => setPending(null)}>
          <p>It comes off the site with the next deployment. The page stays in Genesis and can be published again.</p>
        </ConfirmDialog>
      ) : (
        <ConfirmDialog title={`Keep “${r.title}” on the site?`} confirmLabel="Decline withdrawal" onConfirm={run} onClose={() => setPending(null)}>
          <p>The withdrawal request is declined and the page stays live.</p>
        </ConfirmDialog>
      );
    }
    return action === 'approve' ? (
      <ConfirmDialog title={`Delete “${r.title}”?`} confirmLabel="Delete page" danger onConfirm={run} onClose={() => setPending(null)}>
        <p>
          The page is deleted from Genesis
          {r.publishedVersion ? ' and comes off the site with the next deployment' : ''}.
          {' '}This can’t be undone here.
        </p>
        <p style={{ fontFamily: S.mono, fontSize: 12, color: S.muted }}>{pagePublicPath(r.path)}</p>
      </ConfirmDialog>
    ) : (
      <ConfirmDialog title={`Keep “${r.title}”?`} confirmLabel="Decline deletion" onConfirm={run} onClose={() => setPending(null)}>
        <p>The deletion request is declined and the page stays as it is.</p>
      </ConfirmDialog>
    );
  })();

  if (kind) return <Redirect to="/admin/approvals" />;

  // Approve all takes the publish requests; withdrawals and deletions are decided one at a time.
  const onlyPublish = approvablePublish.length === requests.length;

  let body: React.ReactNode;
  if (error && !data) {
    body = <EmptyState tone="error" title="Couldn’t load requests" action={<Button icon="refresh" onClick={() => refetch()}>Try again</Button>}>{error.message}</EmptyState>;
  } else if (loading && !data) {
    body = <EmptyState icon="hourglass_empty" title="Loading requests…" compact />;
  } else if (requests.length === 0) {
    body = (
      <EmptyState icon="task_alt" title="Nothing waiting">
        Requests to publish, withdraw or delete pages show up here.
      </EmptyState>
    );
  } else {
    body = <div className={c.list} data-testid="approval-requests">{requests.map(renderRequest)}</div>;
  }

  return (
    <Page testId="approvals-screen">
      <PageHeader
        title={tr('Approvals', '审批')}
        description="Requests to publish, withdraw or delete pages. Approved changes reach the site with the next deployment."
        actions={isSuperAdmin && approvablePublish.length > 1 ? (
          <Button icon="done_all" onClick={() => open({ action: 'approveAll', requests: approvablePublish })} data-testid="approve-all">
            {onlyPublish ? `Approve all (${approvablePublish.length})…` : `Approve ${approvablePublish.length} publish requests…`}
          </Button>
        ) : undefined}
      />
      {body}
      {dialog}
    </Page>
  );
};

export default ApprovalsScreen;
